import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Loader2, ExternalLink, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { waLink } from "./invoicesShared";

function fmtTime(iso) {
    try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}

// Jul-2026 (User-Wunsch) — Kunden-WhatsApp: 1:1-Chat direkt aus der Kundenakte,
// mit 24h-Regel-Anzeige. Versand MOCKED bis WhatsApp-Business freigeschaltet ist.
export default function CustomerWhatsAppModal({ customer, onClose }) {
    const [data, setData] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/customers/${customer.id}/whatsapp`);
            setData(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); }
    }, [customer.id]);

    useEffect(() => {
        load();
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.items?.length]);

    const send = async () => {
        const t = text.trim();
        if (!t || sending) return;
        setSending(true);
        try {
            const r = await api.post(`/customers/${customer.id}/whatsapp`, { text: t });
            setData((d) => ({ ...d, items: [...(d?.items || []), r.data] }));
            setText("");
            if (r.data.status === "mocked") toast.info("Nachricht gespeichert (WhatsApp-Versand MOCK — geht live mit der Freischaltung)");
        } catch (e) { toast.error(e.response?.data?.detail || "Senden fehlgeschlagen"); }
        setSending(false);
    };

    const name = `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || customer.company_name || "Kunde";

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="customer-whatsapp-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-emerald-600" /> WhatsApp — {name}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {data?.phone ? `an ${data.phone}` : "Keine Telefonnummer hinterlegt"}
                        {waLink(customer) && (
                            <a href={waLink(customer)} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-emerald-600 hover:underline">
                                <ExternalLink className="w-3 h-3" /> in WhatsApp öffnen
                            </a>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {data?.marketing_blocked ? (
                    <div className="text-xs font-bold bg-amber-50 border border-amber-300 text-amber-800 rounded-sm px-3 py-2" data-testid="wa-gdpr-blocked">
                        📵 DSGVO-Widerspruch: Dieser Kunde darf nicht per WhatsApp kontaktiert werden.
                    </div>
                ) : (
                    <div className={`text-[11px] rounded-sm px-3 py-1.5 border flex items-center gap-1.5 ${data?.window_open ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-200 text-gray-600"}`} data-testid="wa-window-banner">
                        <Clock className="w-3.5 h-3.5 shrink-0" />
                        {data?.window_open
                            ? `Freies Antwortfenster aktiv (24h-Regel) — bis ${fmtTime(data.window_expires_at)}`
                            : "24h-Regel: Außerhalb des Antwortfensters sind nur freigegebene Template-Nachrichten zulässig. Das Fenster öffnet sich, sobald der Kunde schreibt."}
                        {data && !data.live && <span className="ml-auto font-bold text-amber-600">MOCK</span>}
                    </div>
                )}

                <div className="border border-gray-200 rounded-sm bg-gray-50 h-72 overflow-y-auto p-3 space-y-2" data-testid="wa-messages">
                    {!data && <div className="text-center text-xs text-gray-400 pt-8"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
                    {data?.items?.length === 0 && <div className="text-center text-xs text-gray-400 pt-8">Noch keine Nachrichten.</div>}
                    {(data?.items || []).map((m) => (
                        <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-sm px-2.5 py-1.5 border text-sm ${m.direction === "out" ? "bg-emerald-100 border-emerald-200" : "bg-white border-gray-200"}`}>
                                {m.direction === "out" && m.by_name && <div className="text-[10px] font-bold text-emerald-800">{m.by_name}</div>}
                                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                <div className="text-[10px] text-gray-400 text-right mt-0.5">
                                    {fmtTime(m.created_at)}{m.status === "mocked" ? " · MOCK" : m.status === "failed" ? " · fehlgeschlagen" : ""}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={bottomRef} />
                </div>

                <div className="flex items-end gap-2">
                    <textarea value={text} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        rows={2} placeholder="WhatsApp-Nachricht an den Kunden …"
                        disabled={data?.marketing_blocked || !data?.phone}
                        className="flex-1 resize-none rounded-sm border-2 border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 disabled:bg-gray-100"
                        data-testid="wa-input" />
                    <button onClick={send} disabled={sending || !text.trim() || data?.marketing_blocked || !data?.phone}
                        className="h-10 px-4 bg-emerald-600 text-white rounded-sm font-black text-sm disabled:opacity-40 hover:bg-emerald-700 transition-colors inline-flex items-center gap-1.5"
                        data-testid="wa-send">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
